import { Unit, fromMeter, parseLength } from '../core/units';

/** Auswahl-Info, die die App dem Panel liefert. */
export interface SelectionInfo {
  kind: 'none' | 'edge' | 'shape';
  edgeId?: number;
  edgeLength?: number; // Meter
  faceCount?: number;
  dims?: { x: number; y: number; z: number }; // Maße in Meter
  min?: { x: number; y: number; z: number };
  vertexIds?: number[];
  /** Genau eine geschlossene Gruppe gewählt (wie SketchUps Entity-Info „Gruppe“). */
  group?: { id: number; name: string };
  /** Zugehörige Ebene der Auswahl — Deckkraft als Objekt-Eigenschaft editierbar. */
  layer?: { groupId: number | null; opacity: number };
}

export interface EntityInfoCallbacks {
  onEdgeLength(lengthM: number): void;
  onDims(d: { x: number; y: number; z: number }): void;
  /** Auswahl so verschieben, dass ihre Mindest-Ecke an der Position liegt. */
  onPosition(p: { x: number; y: number; z: number }): void;
  /** Gruppennamen (Instanz) setzen. */
  onGroupName(groupId: number, name: string): void;
  /** Vor der ersten Deckkraft-Änderung eines Zugs: Undo-Punkt sichern. */
  onLayerOpacityBegin(): void;
  /** Deckkraft der Ebenen der Auswahl live ändern (0..1). */
  onLayerOpacity(groupId: number | null, opacity: number): void;
}

const AXIS_LABELS: Record<'x' | 'y' | 'z', string> = { x: 'Länge', y: 'Breite', z: 'Höhe' };
const POS_LABELS: Record<'x' | 'y' | 'z', string> = { x: 'X', y: 'Y', z: 'Z' };

/**
 * Entity-Info-Panel rechts: zeigt Maßwerte der Auswahl und macht sie editierbar.
 * - Kante: Länge
 * - Fläche/Körper: Länge × Breite × Höhe
 *   (Eingabe auf einer flachen Achse extrudiert die Fläche zum Körper)
 * - Position X/Y/Z: verschiebt die Auswahl auf den Punkt im Raum
 */
export class EntityInfoPanel {
  el: HTMLElement;
  private signature = '';
  private info: SelectionInfo = { kind: 'none' };
  private lenInput?: HTMLInputElement;
  private dimInputs: Partial<Record<'x' | 'y' | 'z', HTMLInputElement>> = {};
  private posInputs: Partial<Record<'x' | 'y' | 'z', HTMLInputElement>> = {};
  private opacityInput?: HTMLInputElement;
  private kindEl!: HTMLElement;

  constructor(
    root: HTMLElement,
    private cb: EntityInfoCallbacks,
    private units: () => Unit
  ) {
    this.el = document.createElement('div');
    this.el.className = 'panel-section';
    this.el.innerHTML = '<h3>Auswahl</h3>';
    root.prepend(this.el);
    this.render({ kind: 'none' });
  }

  render(info: SelectionInfo): void {
    this.info = info;
    // Deckkraft gehört zur Ebene — Signatur nur über die Ebene (nicht den Wert), sonst
    // würde der Regler beim Ziehen neu gebaut
    const sig = `${info.group?.id ?? ''}|${info.kind}|${info.edgeId ?? ''}|${info.faceCount ?? ''}|${info.dims ? 'd' : ''}|${info.min ? 'p' : ''}|${info.layer ? `L${info.layer.groupId ?? 'loose'}` : ''}`;
    if (sig !== this.signature) {
      this.signature = sig;
      this.build(info);
    }
    // Werte auffrischen (fokussierte Eingaben nicht überschreiben)
    const active = document.activeElement;
    if (info.kind === 'edge' && this.lenInput && this.lenInput !== active) {
      this.lenInput.value = this.display(info.edgeLength ?? 0);
    }
    if (info.kind === 'shape' && info.dims) {
      for (const axis of ['x', 'y', 'z'] as const) {
        const input = this.dimInputs[axis];
        if (input && input !== active) input.value = this.display(info.dims[axis]);
      }
    }
    if (info.min) {
      for (const axis of ['x', 'y', 'z'] as const) {
        const input = this.posInputs[axis];
        if (input && input !== active) input.value = this.display(info.min![axis]);
      }
    }
    if (this.opacityInput && info.layer && this.opacityInput !== active) {
      this.opacityInput.value = String(Math.round(info.layer.opacity * 100));
    }
  }

  private display(meters: number): string {
    const unit = this.units();
    const v = fromMeter(meters, unit);
    const decimals = unit === 'm' ? 3 : unit === 'cm' ? 1 : 0;
    return String(Math.round(v * 10 ** decimals) / 10 ** decimals);
  }

  private parse(input: HTMLInputElement): number | null {
    // Einheiten im Feld („1,5m“, „80cm“) haben Vorrang vor der eingestellten Einheit
    return parseLength(input.value, this.units());
  }

  private build(info: SelectionInfo): void {
    this.lenInput = undefined;
    this.dimInputs = {};
    this.posInputs = {};
    this.opacityInput = undefined;
    const body = document.createElement('div');
    body.className = 'entity-body';

    if (info.kind === 'none') {
      const hint = document.createElement('p');
      hint.className = 'panel-note';
      hint.textContent = 'Kante oder Fläche anklicken, um Maße zu bearbeiten.';
      body.appendChild(hint);
    } else if (info.kind === 'edge' && info.edgeId !== undefined) {
      const label = document.createElement('div');
      label.className = 'entity-kind';
      label.textContent = 'Kante';
      body.appendChild(label);

      const row = this.row('Länge');
      const input = row.querySelector('input')!;
      input.disabled = false;
      input.value = this.display(info.edgeLength ?? 0);
      input.onchange = () => {
        const v = this.parse(input);
        if (v !== null && v > 0) this.cb.onEdgeLength(v);
        else input.value = this.display(this.info.edgeLength ?? 0);
      };
      this.lenInput = input;
      body.appendChild(row);

      if (info.layer) body.appendChild(this.buildLayerOpacityRow());
      if (info.min) body.appendChild(this.buildPositionRows());
    } else if (info.kind === 'shape' && info.dims) {
      const label = document.createElement('div');
      label.className = 'entity-kind';
      label.textContent = info.group
        ? `Gruppe · ${info.faceCount} ${info.faceCount === 1 ? 'Fläche' : 'Flächen'}`
        : info.faceCount === 1 ? 'Fläche' : `Körper · ${info.faceCount} Flächen`;
      body.appendChild(label);
      if (info.group) body.appendChild(this.buildGroupNameRow(info.group));

      for (const axis of ['x', 'y', 'z'] as const) {
        const value = info.dims[axis];
        const flat = value < 1e-4;
        const row = this.row(AXIS_LABELS[axis]);
        const input = row.querySelector('input')!;
        input.value = flat ? '0' : this.display(value);
        if (flat) input.title = 'Wert eingeben, um die Fläche entlang dieser Achse zu einem Körper zu extrudieren';
        input.onchange = () => {
          const v = this.parse(input);
          if (v !== null && v > 0.001) {
            const d = { ...this.info.dims! };
            d[axis] = v;
            this.cb.onDims(d);
          } else {
            input.value = flat ? '0' : this.display(this.info.dims![axis]);
          }
        };
        this.dimInputs[axis] = input;
        body.appendChild(row);
      }

      if (info.layer) body.appendChild(this.buildLayerOpacityRow());
      if (info.min) body.appendChild(this.buildPositionRows());
    }

    this.el.replaceChildren(this.el.querySelector('h3')!, body);
  }

  /** Name der Gruppe (Instanz), wie im SketchUp-Entity-Info. */
  private buildGroupNameRow(group: { id: number; name: string }): HTMLElement {
    const row = this.row('Name');
    const input = row.querySelector('input')!;
    input.inputMode = 'text';
    input.className = 'dim-input name-input';
    input.placeholder = 'z. B. Bett, Küchenblock';
    input.value = group.name;
    input.onchange = () => this.cb.onGroupName(group.id, input.value.trim());
    return row;
  }

  /** Deckkraft der Ebene des aktiven Objekts (Objekt-Eigenschaft im Auswahl-Panel). */
  private buildLayerOpacityRow(): HTMLElement {
    const layer = this.info.layer!;
    const row = this.row('Deckkraft');
    const input = row.querySelector('input')!;
    input.inputMode = 'decimal';
    input.value = String(Math.round(layer.opacity * 100));
    input.title = 'Deckkraft der Ebene dieses Objekts in Prozent (10–100)';
    input.onchange = () => {
      const v = Number.parseFloat(input.value.replace(',', '.'));
      if (Number.isFinite(v)) {
        const pct = Math.min(100, Math.max(10, Math.round(v)));
        this.cb.onLayerOpacityBegin();
        this.cb.onLayerOpacity(layer.groupId, pct / 100);
        input.value = String(pct);
      } else {
        input.value = String(Math.round(layer.opacity * 100));
      }
    };
    this.opacityInput = input;
    return row;
  }

  /** Position X/Y/Z: verschiebt die Auswahl, sodass ihre Mindest-Ecke dort landet. */
  private buildPositionRows(): HTMLElement {
    const wrap = document.createElement('div');
    const head = document.createElement('div');
    head.className = 'entity-subheader';
    head.textContent = 'Position (X/Y/Z)';
    wrap.appendChild(head);

    for (const axis of ['x', 'y', 'z'] as const) {
      const row = this.row(POS_LABELS[axis]);
      const input = row.querySelector('input')!;
      input.className = 'dim-input pos-input';
      input.value = this.display(this.info.min?.[axis] ?? 0);
      input.title = `Absolute ${axis.toUpperCase()}-Koordinate der Auswahl (Mindest-Ecke)`;
      input.onchange = () => {
        const v = this.parse(input);
        if (v !== null) {
          const p = { ...this.info.min! };
          p[axis] = v;
          this.cb.onPosition(p);
        } else {
          input.value = this.display(this.info.min?.[axis] ?? 0);
        }
      };
      this.posInputs[axis] = input;
      wrap.appendChild(row);
    }
    return wrap;
  }

  private row(label: string): HTMLElement {
    const row = document.createElement('div');
    row.className = 'entity-row';
    const l = document.createElement('label');
    l.textContent = label;
    const input = document.createElement('input');
    input.type = 'text';
    input.inputMode = 'decimal';
    input.className = 'dim-input';
    row.append(l, input);
    return row;
  }
}
